import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BillingSettings } from '@/types/models';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { Info, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useDebouncedCallback } from 'use-debounce';

interface BillingSettingsFormProps {
  billingSettings: BillingSettings;
  updateBillingSettings: (settings: Partial<BillingSettings>) => Promise<any>;
}

const BillingSettingsForm: React.FC<BillingSettingsFormProps> = ({
  billingSettings,
  updateBillingSettings
}) => {
  const { organizationId } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState<BillingSettings>(billingSettings);
  const [invoicePreview, setInvoicePreview] = useState<string>('');
  const [prefixError, setPrefixError] = useState<string>('');
  const [numberError, setNumberError] = useState<string>('');
  const [checkingNumber, setCheckingNumber] = useState(false);

  // Validate invoice prefix
  const validatePrefix = (prefix: string): boolean => {
    if (!prefix) {
      setPrefixError('Invoice prefix is required');
      return false;
    }
    if (!/^[A-Za-z0-9\-]+$/.test(prefix)) {
      setPrefixError('Only letters, numbers, and dashes allowed');
      return false;
    }
    if (prefix.length > 10) {
      setPrefixError('Prefix must be 10 characters or less');
      return false;
    }
    setPrefixError('');
    return true;
  };

  // Update invoice preview with debouncing
  const updatePreview = useDebouncedCallback(
    async (prefix: string, startingNumber?: number) => {
      if (!organizationId || !validatePrefix(prefix)) {
        setInvoicePreview('');
        return;
      }

      try {
        const { data, error } = await supabase.rpc('preview_invoice_number', {
          org_id: organizationId,
          test_prefix: prefix,
          test_starting_number: startingNumber || null
        });

        if (error) throw error;
        setInvoicePreview(data || '');
      } catch (error) {
        console.error('Error generating preview:', error);
        setInvoicePreview('');
      }
    },
    300 // 300ms debounce
  );

  // Check if starting number would conflict
  const checkStartingNumber = async (startingNumber: number) => {
    if (!organizationId || !startingNumber) {
      setNumberError('');
      return;
    }

    setCheckingNumber(true);
    try {
      // Get the next available number
      const { data: nextAvailable, error } = await supabase.rpc('get_next_available_invoice_number', {
        org_id: organizationId
      });

      if (error) throw error;

      if (startingNumber < nextAvailable) {
        setNumberError(
          `Starting number ${startingNumber} would conflict with existing invoices. ` +
          `Suggested starting number: ${nextAvailable}`
        );
      } else {
        setNumberError('');
      }
    } catch (error) {
      console.error('Error checking starting number:', error);
    } finally {
      setCheckingNumber(false);
    }
  };

  const handleValueChange = (key: keyof BillingSettings, value: any) => {
    setFormValues(prev => ({
      ...prev,
      [key]: value
    }));

    // Update preview when relevant fields change
    if (key === 'invoice_prefix') {
      validatePrefix(value);
      updatePreview(value, formValues.starting_invoice_number);
    } else if (key === 'starting_invoice_number') {
      updatePreview(formValues.invoice_prefix || 'INV-', value);
      if (value) {
        checkStartingNumber(value);
      }
    }
  };

  // Initial preview generation
  useEffect(() => {
    updatePreview(
      formValues.invoice_prefix || 'INV-', 
      formValues.starting_invoice_number
    );
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate before submitting
    if (!validatePrefix(formValues.invoice_prefix || 'INV-')) {
      toast.error('Please fix the invoice prefix');
      return;
    }

    if (numberError) {
      toast.error('Please fix the starting invoice number');
      return;
    }

    setLoading(true);
    
    try {
      await updateBillingSettings(formValues);
      toast.success('Billing settings updated successfully');
    } catch (error) {
      console.error('Error updating billing settings:', error);
      toast.error('Failed to update billing settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Card>
        <CardHeader>
          <CardTitle>Billing Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Invoice Numbering Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Invoice Numbering</h3>
            
            {invoicePreview && (
              <Alert className="bg-blue-50 border-blue-200">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  <span className="font-medium">Next invoice number preview:</span> {invoicePreview}
                </AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="invoice-prefix">
                  Invoice Number Prefix
                  <span className="text-sm font-normal text-muted-foreground ml-1">(Required)</span>
                </Label>
                <Input
                  id="invoice-prefix"
                  value={formValues.invoice_prefix || ''}
                  onChange={(e) => handleValueChange('invoice_prefix', e.target.value)}
                  placeholder="INV-"
                  className={prefixError ? 'border-red-500' : ''}
                  maxLength={10}
                />
                {prefixError && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {prefixError}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  Examples: INV-, STUDIO-, 2025-
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="starting-number">
                  Starting Invoice Number
                  <span className="text-sm font-normal text-muted-foreground ml-1">(Optional)</span>
                </Label>
                <Input
                  id="starting-number"
                  type="number"
                  value={formValues.starting_invoice_number || ''}
                  onChange={(e) => handleValueChange('starting_invoice_number', e.target.value ? Number(e.target.value) : undefined)}
                  placeholder="1"
                  min={1}
                  className={numberError ? 'border-red-500' : ''}
                />
                {checkingNumber && (
                  <p className="text-sm text-muted-foreground">Checking availability...</p>
                )}
                {numberError && (
                  <p className="text-sm text-red-600 flex items-start gap-1">
                    <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                    <span>{numberError}</span>
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  For migrations: enter your last invoice number + 1
                </p>
              </div>
            </div>
          </div>

          <div className="border-t pt-6" />

          {/* Other Billing Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="due-days">Default Due Days</Label>
              <Input
                id="due-days"
                type="number"
                value={formValues.default_due_days || 30}
                onChange={(e) => handleValueChange('default_due_days', Number(e.target.value))}
                min={1}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="currency">Currency</Label>
              <Select
                value={formValues.currency || 'USD'}
                onValueChange={(value) => handleValueChange('currency', value)}
              >
                <SelectTrigger id="currency">
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">US Dollar ($)</SelectItem>
                  <SelectItem value="EUR">Euro (€)</SelectItem>
                  <SelectItem value="GBP">British Pound (£)</SelectItem>
                  <SelectItem value="CAD">Canadian Dollar (C$)</SelectItem>
                  <SelectItem value="AUD">Australian Dollar (A$)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tax-rate">Tax Rate (%)</Label>
              <Input
                id="tax-rate"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formValues.tax_rate || 0}
                onChange={(e) => handleValueChange('tax_rate', Number(e.target.value))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="billing-email">Billing Email</Label>
              <Input
                id="billing-email"
                type="email"
                value={formValues.billing_email || ''}
                onChange={(e) => handleValueChange('billing_email', e.target.value)}
                placeholder="billing@studio.com"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="payment-instructions">Payment Instructions</Label>
            <Textarea
              id="payment-instructions"
              value={formValues.payment_instructions || ''}
              onChange={(e) => handleValueChange('payment_instructions', e.target.value)}
              placeholder="Instructions for how to submit payments"
              rows={3}
              className="resize-none"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="invoice-footer">Invoice Footer Text</Label>
            <Textarea
              id="invoice-footer"
              value={formValues.invoice_footer || ''}
              onChange={(e) => handleValueChange('invoice_footer', e.target.value)}
              placeholder="Thank you for your business!"
              rows={2}
              className="resize-none"
            />
          </div>

          <div className="space-y-6">
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label className="text-base" htmlFor="auto-reminders">
                  Automatic Payment Reminders
                </Label>
                <p className="text-sm text-muted-foreground">
                  Send automatic reminders for upcoming and overdue invoices
                </p>
              </div>
              <Switch
                id="auto-reminders"
                checked={formValues.auto_reminders || false}
                onCheckedChange={(checked) => handleValueChange('auto_reminders', checked)}
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label className="text-base" htmlFor="allow-partial">
                  Allow Partial Payments
                </Label>
                <p className="text-sm text-muted-foreground">
                  Let parents make partial payments on invoices
                </p>
              </div>
              <Switch
                id="allow-partial"
                checked={formValues.allow_partial_payments || false}
                onCheckedChange={(checked) => handleValueChange('allow_partial_payments', checked)}
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label className="text-base" htmlFor="auto-send">
                  Auto-Send Invoices
                </Label>
                <p className="text-sm text-muted-foreground">
                  Automatically send invoices when created
                </p>
              </div>
              <Switch
                id="auto-send"
                checked={formValues.auto_send_invoices || false}
                onCheckedChange={(checked) => handleValueChange('auto_send_invoices', checked)}
              />
            </div>
          </div>

          <Button 
            type="submit" 
            disabled={loading || !!prefixError || !!numberError || checkingNumber}
            className="w-full sm:w-auto"
          >
            {loading ? 'Saving...' : 'Save Billing Settings'}
          </Button>
        </CardContent>
      </Card>
    </form>
  );
};

export default BillingSettingsForm;